import React from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { amber, blue, green, grey, red } from '@material-ui/core/colors';

export const LINKED_STATUS_ENUM = {
	notLinked: 'notLinked',
	deliveryLinked: 'deliveryLinked',
	railcarLinked: 'railcarLinked',
	linkError: 'linkError',
};

const statusToDisplayMap = {
	[LINKED_STATUS_ENUM.notLinked]: 'Not Linked',
	[LINKED_STATUS_ENUM.deliveryLinked]: 'Delivery Linked',
	[LINKED_STATUS_ENUM.railcarLinked]: 'Railcar Linked',
	[LINKED_STATUS_ENUM.linkError]: 'Link Error',
};

const statusToColorMap = {
	[LINKED_STATUS_ENUM.notLinked]: grey[500],
	[LINKED_STATUS_ENUM.deliveryLinked]: blue[500],
	[LINKED_STATUS_ENUM.railcarLinked]: green[500],
	[LINKED_STATUS_ENUM.linkError]: red[500],
};

const MessageTooltip = withStyles(() => ({
	tooltip: {
		fontSize: 13,
		lineHeight: 1.4,
		maxWidth: 400,
	},
}))(Tooltip);

/**
 * Compact linked-status cell for the BOL listing pages. The full link
 * error/detail message only appears in a tooltip on hover so long messages
 * don't blow up the row height.
 */
function BolLinkedStatus({ linkedStatus, message, statusColorOverrides }) {
	const color =
		(statusColorOverrides && statusColorOverrides[linkedStatus]) ||
		statusToColorMap[linkedStatus] ||
		grey[500];

	const label = (
		<span style={{ color, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
			{statusToDisplayMap[linkedStatus] || 'Missing'}
		</span>
	);

	if (!message) {
		return label;
	}

	// A "Warning:" message is a non-blocking note on a row that linked anyway
	// (e.g. InterState's gallons-mismatch check) — amber so it reads as
	// "check this" rather than detail.
	const isWarningNote = typeof message === 'string' && message.startsWith('Warning:');

	return (
		<MessageTooltip title={message} placement="top-start" interactive>
			<span style={{ whiteSpace: 'nowrap', cursor: 'help' }}>
				{label}
				<InfoOutlinedIcon
					style={{
						fontSize: 16,
						verticalAlign: 'text-bottom',
						marginLeft: 4,
						color: isWarningNote ? amber[800] : grey[600],
					}}
				/>
			</span>
		</MessageTooltip>
	);
}

BolLinkedStatus.propTypes = {
	linkedStatus: PropTypes.string,
	message: PropTypes.string,
	statusColorOverrides: PropTypes.object,
};

export default BolLinkedStatus;
